import { c as _c } from "react/compiler-runtime";
import { FormControl, FormLabel, MenuItem, Radio, RadioGroup, Select, TextField, Typography, styled, Stack, Box } from '@mui/material';
import type { Account } from '@repo/api-gw-sdk';
import { AccountState, Provider } from '@repo/api-gw-sdk';
import { useCallback, useEffect, useState } from 'react';
import { useDAL } from '@/contexts/useDAL';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import { CloudProviders } from '@/data/inventory/data';
import type { BackupVault } from '@/data/vaults/backupVault';
import { VaultColors } from '@/theme/colorSchemes';
import { AwsKmsTextField } from './awsKmsTextField';
import { useBackupVaultColorUpdate } from './useBackupVaultColorUpdate';
import { VaultAccountBucketSelector } from './vaultAccountBucketSelector';
import { VaultAdvanceOptions } from './vaultAdvanceOptions';
import { VaultManagedTypeSelect, type VaultManagedType } from './vaultManagedTypeSelect';
import { VXButtons } from '../buttons/vxButtons';
import { GeneralEmptyState } from '../generalEmptyState';
import { Loader } from '../layout/loading';
import { PanelWrapper } from '../panels/panelWrapper';
import { useSupportedRegions } from '../selectors/supportedRegions';
import { BrowserGrid } from '../shared/browserGrid';
const ColorRadioButton = styled(Radio)<{
  value: string;
}>(props => ({
  display: 'inline',
  width: '20px',
  height: '20px',
  backgroundColor: props.value,
  margin: '6px',
  '&:first-child': {
    marginLeft: 0
  },
  '&:hover': {
    backgroundColor: props.value,
    opacity: 0.7
  },
  '& svg': {
    display: 'none'
  },
  '&.Mui-checked': {
    border: '2px solid gray'
  },
  '&.Mui-disabled': {
    opacity: 0.5
  }
}));
export function CreateBackupVaultPanel({
  backupVault,
  onChange,
  onClose
}: {
  backupVault: BackupVault;
  onChange: () => void;
  onClose: () => void;
}) {
  const dal = useDAL();
  const {
    data: vaultAccounts,
    isLoading: loadingAccounts
  } = dal.vaults.useAccounts();
  if (loadingAccounts) {
    return <Loader />;
  }
  if (!vaultAccounts?.accounts.length) {
    return <GeneralEmptyState title='No vault accounts found' description='Create a vault account first, and then come here to create a vault' button={null} />;
  }
  return <CreateBackupVaultPanelInner vaultAccounts={vaultAccounts.accounts} backupVault={{
    ...backupVault,
    vaultAttributes: {
      ...backupVault.vaultAttributes,
      cloudProvider: vaultAccounts.accounts[0].cloudProvider
    }
  }} onChange={onChange} onClose={onClose} />;
}
function CreateBackupVaultPanelInner({
  vaultAccounts,
  backupVault,
  onChange,
  onClose
}: {
  vaultAccounts: Account[];
  backupVault: BackupVault;
  onChange: () => void;
  onClose: () => void;
}) {
  const [data, setData] = useState(backupVault);
  const [error, setError] = useState<string | undefined>();
  const dal = useDAL();
  const {
    AzureBackupVaultCmkSupport: azureBackupVaultCmkSupport = false
  } = useFeatureFlags();
  const [isSaving, setIsSaving] = useState(false);
  const [customKey, setCustomKey] = useState(!!data.vaultAttributes.aws?.encryptionKey || !!data.vaultAttributes.gcp?.encryptionKey || !!data.vaultAttributes.azure?.encryptionKey && azureBackupVaultCmkSupport);
  const supportedRegions = useSupportedRegions();
  const {
    updateVaultColor
  } = useBackupVaultColorUpdate();
  const selectedProviderVaultAccounts = vaultAccounts.filter(a => a.cloudProvider === data.vaultAttributes.cloudProvider);
  const supportedCloudProviders = Array.from(new Set(vaultAccounts.map(a => a.cloudProvider)));
  const selfManagedAccounts = selectedProviderVaultAccounts?.filter(account => account.isCustomerOwned);
  const eonManagedAccounts = selectedProviderVaultAccounts?.filter(account => !account.isCustomerOwned);
  const hasEonManaged = !!eonManagedAccounts?.length;
  const hasSelfManaged = !!selfManagedAccounts?.length;
  const showVaultTypeSelect = hasSelfManaged && hasEonManaged;
  const getVaultType = useCallback((): VaultManagedType | undefined => {
    return hasEonManaged ? 'eon-managed' : hasSelfManaged ? `self-managed-${data.vaultAttributes.cloudProvider}` : undefined;
  }, [hasEonManaged, hasSelfManaged, data.vaultAttributes.cloudProvider]);
  const [vaultType, setVaultType] = useState<VaultManagedType | undefined>(getVaultType());
  useEffect(() => {
    setVaultType(getVaultType());
  }, [getVaultType]);

  /** AWS self-managed (BYOV) vault flow — matches VaultManagedTypeSelect value for this cloud. */
  const isAwsSelfManagedVault = data.vaultAttributes.cloudProvider === Provider.Aws && vaultType === `self-managed-${Provider.Aws}`;
  useEffect(() => {
    setData({
      ...data,
      vaultAccountId: vaultType === 'eon-managed' ? eonManagedAccounts?.[0]?.id || '' : selfManagedAccounts?.find(x => x.state === AccountState.ACCOUNT_STATE_CONNECTED)?.id || ''
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.vaultAttributes.cloudProvider, vaultType]);
  function validate(): boolean {
    if (!data.name || data.name.length < 5) {
      setError('Name must be at least 5 characters long');
      return false;
    }
    const regexp = /^[a-zA-Z0-9-_ ]+$/;
    if (data.name.search(regexp) === -1) {
      setError('Name can only contain letters, numbers, and hyphens');
      return false;
    }
    if (!data.vaultAttributes.cloudProvider) {
      setError('Please select a cloud provider');
      return false;
    }
    if (!data.region) {
      setError('Please select a region');
      return false;
    }
    if (azureBackupVaultCmkSupport && customKey && data.vaultAttributes.cloudProvider === Provider.Azure) {
      const az = data.vaultAttributes.azure;
      if (!az?.encryptionKey?.trim() || !az?.cmkKeyVaultTenantId?.trim() || !az?.cmkAppRegistrationId?.trim()) {
        setError('Set Key Vault tenant, app, and key URI for customer-managed encryption');
        return false;
      }
    }
    return true;
  }
  function clearValidationError() {
    setError(undefined);
  }
  async function save() {
    if (!validate()) {
      return;
    }
    setIsSaving(true);
    let createPayload = data;
    try {
      if (data.vaultAttributes.cloudProvider === Provider.Azure && (!azureBackupVaultCmkSupport || !customKey)) {
        const {
          encryptionKey: _ek,
          cmkKeyVaultTenantId: _ct,
          cmkAppRegistrationId: _ca,
          ...azureRest
        } = data.vaultAttributes.azure ?? {};
        createPayload = {
          ...data,
          vaultAttributes: {
            ...data.vaultAttributes,
            azure: azureRest
          }
        };
      }
      const res = await dal.vaults.create(createPayload);
      createPayload = {
        ...data,
        id: res?.vault?.id || ''
      };
    } catch (e) {
      const err = e as {
        message: string;
        body?: {
          error: string;
        };
      };
      setIsSaving(false);
      setError(err.body?.error || err.message || 'Failed to create backup vault');
      return;
    }
    await updateVaultColor(createPayload);
    onChange();
  }
  return <>
      <PanelWrapper header={{
      onClose,
      title: 'Create vault'
    }} footer={{
      primaryButton: {
        disabled: isSaving || data.name?.length < 5 || !data.region || !data.vaultAttributes.cloudProvider || customKey && !data.vaultAttributes.aws?.encryptionKey && data.vaultAttributes.cloudProvider === Provider.Aws || customKey && !data.vaultAttributes.gcp?.encryptionKey && data.vaultAttributes.cloudProvider === Provider.Gcp || customKey && (!data.vaultAttributes.azure?.encryptionKey?.trim() || !data.vaultAttributes.azure?.cmkKeyVaultTenantId?.trim() || !data.vaultAttributes.azure?.cmkAppRegistrationId?.trim()) && data.vaultAttributes.cloudProvider === Provider.Azure && azureBackupVaultCmkSupport,
        'data-testid': 'save-vault',
        text: 'Create Vault',
        onClick: () => {
          void save();
        }
      },
      secondaryButton: {
        'data-testid': 'cancel-vault',
        disabled: isSaving,
        text: 'Cancel',
        onClick: onClose
      },
      children: <Typography variant='body1'>
              Once the vault is created, only its name and color can be changed.
            </Typography>
    }}>
        <Stack marginTop='min(400px, 12vw)' marginBottom='40px' alignItems='center' flexGrow={1}>
          <Stack width='840px' gap='40px' sx={{
          overflow: 'hidden'
        }}>
            <Stack gap='40px'>
              <Stack direction='row' gap='40px'>
                <FormControl className='w-1/2'>
                  <FormLabel>{'Name'}</FormLabel>
                  <TextField slotProps={{
                  input: {
                    sx: {
                      height: '48px',
                      padding: '8px'
                    }
                  },
                  htmlInput: {
                    'data-testid': 'vault-name'
                  }
                }} disabled={isSaving} defaultValue={data.name} onChange={event => {
                  clearValidationError();
                  setData({
                    ...data,
                    name: event.target.value
                  });
                }} />
                </FormControl>
                <FormControl className='w-1/2 flex flex-row items-center justify-between'>
                  <Stack>
                    <FormLabel>{'Color'}</FormLabel>
                    <VaultColorsSelector data={data} setData={setData} disabled={isSaving} />
                  </Stack>
                </FormControl>
              </Stack>
              <Stack direction='row' gap='40px'>
                <FormControl className='w-1/2'>
                  <FormLabel>Cloud</FormLabel>
                  <Select sx={{
                  paddingLeft: '8px'
                }} size='medium' disabled={isSaving} value={data.vaultAttributes.cloudProvider} data-testid='vault-cloud-provider' onChange={event => {
                  clearValidationError();
                  setData({
                    ...data,
                    region: '',
                    vaultAttributes: {
                      cloudProvider: event.target.value
                    }
                  });
                }}>
                    {supportedCloudProviders.map(cloud => <MenuItem key={cloud} value={cloud} data-testid={`vault-cloud-provider-${cloud}`}>
                        {CloudProviders[cloud].displayName}
                      </MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl className='w-1/2'>
                  <FormLabel>{'Region'}</FormLabel>
                  <Select sx={{
                  paddingLeft: '8px'
                }} size='medium' disabled={isSaving} data-testid='vault-region' value={data.region} onChange={event => {
                  clearValidationError();
                  setData({
                    ...data,
                    region: event.target.value
                  });
                }}>
                    {supportedRegions[data.vaultAttributes.cloudProvider]?.map((region, i) => {
                    return <MenuItem value={region} key={i} data-testid={`vault-region-${region}`}>
                            {region}
                          </MenuItem>;
                  })}
                  </Select>
                </FormControl>
              </Stack>
              <BrowserGrid gap='40px'>
                {showVaultTypeSelect && <VaultManagedTypeSelect title='Type' selfManagedVaultAccounts={selfManagedAccounts} showEonManaged={hasEonManaged} restrictedCloudProviders={[data.vaultAttributes.cloudProvider]} vaultManagedType={vaultType} onChange={vaultType => {
                setVaultType(vaultType);
              }} />}
                {isAwsSelfManagedVault && <VaultAccountBucketSelector data={data} setData={setData} vaultAccounts={selfManagedAccounts} />}
                {isAwsSelfManagedVault && <FormControl>
                    <FormLabel>{'KMS Key (optional)'}</FormLabel>
                    <AwsKmsTextField backupVault={data} setBackupVault={setData} />
                  </FormControl>}
              </BrowserGrid>
              {(!isAwsSelfManagedVault && data.vaultAttributes.cloudProvider === Provider.Aws || data.vaultAttributes.cloudProvider === Provider.Gcp || data.vaultAttributes.cloudProvider === Provider.Azure && azureBackupVaultCmkSupport) && <VaultAdvanceOptions disabled={isSaving} backupVault={data} setBackupVault={setData} customKey={customKey} setCustomKey={setCustomKey} vaultAccountId={data.vaultAccountId} defaultAzureTenantId={selectedProviderVaultAccounts.find(a => a.id === data.vaultAccountId)?.tenantId} />}
            </Stack>
            {error && <Box sx={{
            height: '25px',
            marginTop: '12px'
          }}>
                <Typography color='error' variant='body2'>
                  <i className='material-symbols-info-outline text-[22px] text-(--mui-palette-error-main) align-middle mb-[2px]' />
                  {error}
                </Typography>
              </Box>}
          </Stack>
        </Stack>
      </PanelWrapper>
    </>;
}
const VaultColorsSelector = ({
  data,
  setData,
  disabled
}: {
  data: BackupVault;
  setData: (data: BackupVault) => void;
  disabled: boolean;
}) => {
  return <RadioGroup className='flex-row' aria-labelledby='demo-controlled-radio-buttons-group' name='controlled-radio-buttons-group' value={data.backgroundColor || VaultColors.PINK} onChange={event => {
    setData({
      ...data,
      backgroundColor: event.target.value
    });
  }}>
      {Object.values(VaultColors).map(color => <ColorRadioButton data-testid={`vault-color-btn`} key={color} value={color} slotProps={{
      input: {
        ['data-testid' as string]: `vault-color-${color}`
      }
    }} disabled={disabled} />)}
    </RadioGroup>;
};
export const VaultColorSelectorWithButtons = t0 => {
  const $ = _c(13);
  const {
    initialData,
    saveData,
    onCancel,
    disabled
  } = t0;
  const [data, setData] = useState(initialData);
  let t1;
  if ($[0] !== data || $[1] !== saveData) {
    t1 = () => {
      saveData({
        ...data
      });
    };
    $[0] = data;
    $[1] = saveData;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const onApprove = t1;
  let t2;
  if ($[3] !== data || $[4] !== disabled) {
    t2 = <VaultColorsSelector data={data} setData={setData} disabled={disabled} />;
    $[3] = data;
    $[4] = disabled;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== disabled || $[7] !== onApprove || $[8] !== onCancel) {
    t3 = <VXButtons onApprove={onApprove} onCancel={onCancel} approveDisabled={disabled} />;
    $[6] = disabled;
    $[7] = onApprove;
    $[8] = onCancel;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== t2 || $[11] !== t3) {
    t4 = <Stack direction="row" alignItems="center" gap="12px">{t2}{t3}</Stack>;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
};